import { Draggable } from '@hello-pangea/dnd';
import { TrashIcon } from '@heroicons/react/24/outline';
import { Maybe } from 'maybeasy';
import { observer } from 'mobx-react';
import { useContext } from 'react';
import { ExperienceResource } from '../../../Types';
import ExperienceCard from '../Shared/ExperienceCard';
import DragIndicatorIcon from '../../../../../../../Cutesies/DragIndicatorIcon';
import DeleteProgramModal from '../../DeleteProgramModal';
import { handleRemoveExperience } from '../../../Experience/AddRemoveProgram';
import { useLearningCollection } from '../../../LearningCollectionContext';
import { trackIntercomEvent } from '../../../../../../../../services/intercomTrackingService';
import { TranslationsContext, translation } from '../../../../../../../../Translations';

interface Props {
  experienceResource: Maybe<ExperienceResource>;
  index: number;
  sectionIndex: number;
  isDragging: boolean;
  editable?: boolean;
}

function DraggableGridItem({
  experienceResource,
  index,
  sectionIndex,
  isDragging,
  editable = false,
}: Props) {
  const ts = useContext(TranslationsContext);
  const { resourceState, setResourceState } = useLearningCollection();
  const programTitle = experienceResource
    .map((r) => r.payload.title)
    .getOrElse(() => translation('Item')(ts));
  const experienceId = experienceResource.map((r) => r.payload.id).getOrElse(() => index);

  const handleRemove = () => {
    trackIntercomEvent('Builder V2 - Main Screen - Remove Program from Card', {
      programId: experienceId,
      programName: programTitle,
      sectionIndex,
      displayType: 'grid',
    });
    handleRemoveExperience(experienceId, resourceState, setResourceState, sectionIndex);
  };

  return (
    <Draggable draggableId={String(experienceId)} index={index} isDragDisabled={!editable}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={`relative group h-full ${snapshot.isDragging ? 'opacity-50' : ''}`}
          style={provided.draggableProps.style}
        >
          {editable && (
            <DeleteProgramModal programName={programTitle} onConfirm={handleRemove}>
              {({ onOpen }) => (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpen(e);
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                  }}
                  className="absolute top-2 left-2 z-50 p-1.5 bg-card/90 backdrop-blur-sm rounded-md border border-border opacity-0 group-hover:opacity-100 hover:bg-muted transition-all pointer-events-auto"
                  aria-label={`Remove ${programTitle}`}
                  data-intercom-target="builder-v2-remove-program-button-grid"
                >
                  <TrashIcon className="w-4 h-4 text-muted-foreground" />
                </button>
              )}
            </DeleteProgramModal>
          )}

          {editable && (
            <div
              {...provided.dragHandleProps}
              className="absolute top-2 right-2 z-10 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity bg-card/90 backdrop-blur-sm rounded-lg p-1.5 border border-border cursor-grab active:cursor-grabbing focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              aria-label={`Drag to reorder program ${programTitle} in grid`}
            >
              <DragIndicatorIcon className="text-muted-foreground" />
            </div>
          )}

          <div className={`h-full ${isDragging ? 'pointer-events-none' : ''}`}>
            <ExperienceCard
              experienceResource={experienceResource}
              sectionIndex={sectionIndex}
              editable={editable}
            />
          </div>
        </div>
      )}
    </Draggable>
  );
}

export default observer(DraggableGridItem);
